import { useState } from "react";
import { toast } from "sonner";
import { Share2, Users, Copy, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const Card = ({ id, icon: I, title, sub, children }) => (
  <article data-testid={id} className="min-w-0 rounded-2xl bg-surface border border-slate-200 p-5 sm:p-6">
    <div className="flex items-center gap-3">
      <span className="grid place-items-center w-10 h-10 rounded-xl bg-navy-50 text-navy-700"><I className="w-5 h-5" /></span>
      <div><h3 className="font-semibold text-slate-900">{title}</h3><p className="text-sm text-slate-500">{sub}</p></div>
    </div>
    <div className="mt-4">{children}</div>
  </article>
);

export const InviteCard = () => {
  const url = `${window.location.origin}/login`;
  const share = async () => {
    const text = "I use Daybreyk for my timetable, homework and revision. Make your own account:";
    if (navigator.share) { try { await navigator.share({ title: "Daybreyk", text, url }); return; } catch { /* fall back */ } }
    await navigator.clipboard?.writeText(`${text} ${url}`).catch(() => {});
    toast.success("Invite link copied");
  };
  return (
    <Card id="invite-card" icon={Share2} title="Invite friends" sub="Send classmates a link to make their own Daybreyk.">
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="invite-share-button" onClick={share} className="inline-flex items-center gap-2 rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark"><Share2 className="w-4 h-4" />Share invite</button>
        <span data-testid="invite-url" className="font-mono text-xs text-slate-500 break-all">{url}</span>
      </div>
    </Card>
  );
};

export const ShareTimetableCard = ({ onImported }) => {
  const [code, setCode] = useState("");
  const [mine, setMine] = useState("");
  const [busy, setBusy] = useState(false);
  const make = async () => {
    try { const { data } = await api.post("/timetable/share"); setMine(data?.code ?? ""); } catch (e) { toast.error(formatApiError(e)); }
  };
  const imp = async () => {
    if (!window.confirm("Replace your timetable with the shared one?")) return;
    setBusy(true);
    try { const { data } = await api.post("/timetable/import", { code }); toast.success(`Copied Week ${(data?.weeks ?? []).join(" + ")}`); setCode(""); onImported?.(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <Card id="share-timetable-card" icon={Users} title="Class timetable" sub="Same class as a friend? Share your timetable with a code, or copy theirs.">
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="tt-share-button" onClick={make} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-100"><Copy className="w-4 h-4" />Get my share code</button>
        {mine && <span data-testid="tt-share-code" className="rounded-xl bg-gold-400 px-3 py-2 font-mono text-sm font-bold tracking-widest text-black">{mine}</span>}
      </div>
      <div className="mt-3 flex gap-2">
        <input data-testid="tt-import-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={12} placeholder="Friend's code"
          className="flex-1 min-w-0 rounded-xl border border-slate-300 bg-transparent px-3 py-2 font-mono text-sm uppercase tracking-widest text-slate-900 outline-none focus:border-gold-400" />
        <button data-testid="tt-import-button" onClick={imp} disabled={busy || code.length < 4} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-black disabled:opacity-40">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Copy timetable</button>
      </div>
    </Card>
  );
};
